/**
 * Format a hierarchical build list for copy/export (human-readable compact sheet).
 */

import {
  buildCategoryForest,
  type HierarchyNode,
} from './modificationHierarchy';

export interface CompactBuildListOptions {
  includeSpecifications?: boolean;
  includeDescriptions?: boolean;
  includeNotes?: boolean;
  includePrices?: boolean;
  indent?: string;
}

export interface CompactBuildListMod {
  id?: string | number;
  title: string;
  description?: string | null;
  category?: string;
  vendor?: string;
  cost?: number | null;
  notes?: string | null;
  parent_modification_id?: string | number | null;
  specifications?: string[] | Record<string, unknown> | string | null;
  sort_order?: number | null;
}

function formatSpecs(specifications: CompactBuildListMod['specifications']): string[] {
  if (!specifications) return [];
  if (Array.isArray(specifications)) {
    return specifications.map((s) => String(s).trim()).filter(Boolean);
  }
  if (typeof specifications === 'string') {
    try {
      const parsed = JSON.parse(specifications);
      return formatSpecs(parsed);
    } catch {
      return specifications
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  if (typeof specifications === 'object') {
    return Object.entries(specifications)
      .map(([k, v]) => `${k}: ${v}`)
      .filter(Boolean);
  }
  return [];
}

function formatLine(mod: CompactBuildListMod, options: CompactBuildListOptions): string {
  const brand = (mod.vendor || '').trim();
  const title = (mod.title || '').trim() || 'Untitled part';
  let line = brand ? `${brand} ${title}` : title;
  if (options.includePrices && mod.cost != null && Number(mod.cost) > 0) {
    line += ` - $${Number(mod.cost).toLocaleString('en-US')}`;
  }
  return line;
}

function renderNode(
  node: HierarchyNode<CompactBuildListMod>,
  options: CompactBuildListOptions,
  depth: number,
  indent: string,
  lines: string[]
): void {
  const pad = indent.repeat(depth);
  lines.push(`${pad}${formatLine(node.mod, options)}`);

  if (options.includeDescriptions && node.mod.description?.trim()) {
    lines.push(`${pad}${indent}${node.mod.description.trim()}`);
  }
  if (options.includeNotes && node.mod.notes?.trim()) {
    lines.push(`${pad}${indent}Note: ${node.mod.notes.trim()}`);
  }
  if (options.includeSpecifications) {
    for (const spec of formatSpecs(node.mod.specifications)) {
      lines.push(`${pad}${indent}${spec}`);
    }
  }

  for (const child of node.children) {
    renderNode(child, options, depth + 1, indent, lines);
  }
}

/** Produce a clean indented build list suitable for Notes / Docs / email. */
export function formatCompactBuildList(
  modifications: CompactBuildListMod[],
  options: CompactBuildListOptions = {}
): string {
  const opts: Required<CompactBuildListOptions> = {
    includeSpecifications: options.includeSpecifications ?? true,
    includeDescriptions: options.includeDescriptions ?? false,
    includeNotes: options.includeNotes ?? false,
    includePrices: options.includePrices ?? false,
    indent: options.indent ?? '  ',
  };

  const sorted = [...modifications].sort((a, b) => {
    const sa = a.sort_order ?? 0;
    const sb = b.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });

  const forest = buildCategoryForest(sorted);
  const lines: string[] = [];

  forest.forEach((cat, index) => {
    if (index > 0) lines.push('');
    lines.push(cat.category);
    lines.push('');
    for (const root of cat.roots) {
      renderNode(root, opts, 0, opts.indent, lines);
    }
  });

  return lines.join('\n').trim() + (lines.length ? '\n' : '');
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
